/**
 * SelectionActionBar — the one contextual surface for a selection.
 *
 * Fourth landing for the selection verbs, and the reasoning is the durable
 * part (the first three are in docs/plans/2026-07-29-resize-affordances.md):
 *
 *   1. Bespoke fixed bar (#127) — right interaction, wrong surface.
 *   2. Dockable Layout panel (#129) — right surface family, wrong interaction:
 *      a persistent tab far from the selection, mostly showing its empty state.
 *   3. DS ActionBar carrying SIZING ONLY, with gap in a docked Grid panel —
 *      right surface, wrong scope. One click lit up three chromes (this bar,
 *      the Grid panel, the section label tab), and the panel reproduced the
 *      exact failure that retired the Layout tab.
 *   4. This: ONE bar whose contents follow WHAT is selected (selectionView):
 *      a widget, a section block, or a multi-selection. Verbs are ordered by
 *      frequency and every one of them works with a finger — no hover, no
 *      modifier, no drag required (the drag stays as the accelerator).
 *
 * Widget:  Edit · Delete · Duplicate · Size (presets, W×H) · Boundaries · Section
 * Section: Gap (typed, with steppers) · Boundaries · Move up/down · Duplicate · Delete
 * Multi:   Delete · Duplicate · Section
 * Page:    Section spacing (the page grid's row gap), when a page node is
 *          selected and nothing inside it is
 *
 * "Boundaries" is a REVEAL, not a mode: it draws the grid's shared-edge
 * splitters (invisible by default, and touch has no hover to find them) and
 * turns itself off when the selection leaves that grid. Gap stays a typed
 * value — one scalar per axis is a number to enter, not a drag to arm; the
 * Alt+drag gutter gesture remains as the desktop accelerator.
 *
 * `bottomOffset` keeps the bar clear of the zoom controls; the coach pills
 * above it read the bar's measured height (bottomStack) instead of assuming
 * one row, because the verbs wrap on a phone.
 */
import { useEffect, useRef } from "react";
import { ActionBar, Button, Input, Toggle, ToggleGroup } from "@duro-app/ui";
import { gapOf, useGridStore } from "@fredericrous/grid";
import { css, html } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { microSpacing, spacing } from "@duro-app/tokens/tokens/spacing.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";

import { BAR_BOTTOM, publishBarHeight } from "./bottomStack.js";
import { useDockedInset } from "./dockedInset.js";
import { useSelectedPage, type SelectedPage } from "./pageSelection.js";
import { requestEdit } from "./editRequest.js";
import { allocateFieldName } from "./fieldNames.js";
import { openSettings } from "./settingsTarget.js";
import { openQuickEdit } from "./quickEditTarget.js";
import { flushPendingEdits, patchContentProps } from "./pendingEdits.js";
import {
  registeredWidgetQuickEdit,
  registeredWidgetSettings,
} from "./widgets/catalogRegistry.js";
import {
  hideBoundaries,
  revealBoundaries,
  useRevealedBoundaries,
} from "./resizeMode.js";
import type { InlineJustify } from "@website-builder/schemas";
import { alignInlineGrid } from "./inlineRows.js";
import { duplicateInline, duplicateSection } from "./sectionDuplicate.js";
import { canMoveSection, swapSection } from "./sectionOrder.js";
import {
  editVerbFor,
  useSelectionView,
  widgetKindName,
  type InlineSelection,
  type MultiSelection,
  type SectionSelection,
  type SelectionView,
  type WidgetSelection,
} from "./selectionView.js";
import {
  WIDTH_PRESETS,
  activeWidthPreset,
  applyExplicitSize,
  applyWidthPreset,
  type GridPosition,
  type SizeConstraints,
  type WidthPreset,
} from "./selectionSize.js";
import { useIsMobile } from "./useIsMobile.js";
import { usePointerCoarse } from "./usePointerCoarse.js";
import { useResizeMetricsApi } from "./useResizeMetrics.js";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import {
  deleteSelection,
  selectParent,
  useSelectionKeys,
} from "./useSelectionKeys.js";

// Longhand only, per react-strict-dom; nothing here relies on flex-grow
// (html.* forces flex-grow: 0 on web).
const s = css.create({
  verbs: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "center",
    rowGap: microSpacing.px6,
    columnGap: microSpacing.px6,
    maxWidth: `calc(100vw - ${spacing.xl})`,
  },
  group: {
    display: "flex",
    alignItems: "center",
    gap: microSpacing.px6,
    paddingLeft: "0.5rem",
    borderLeftWidth: borders.hairline,
    borderLeftStyle: "solid",
    borderLeftColor: colors.border,
  },
  field: {
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
  },
  fieldLabel: {
    fontSize: typography.fontSizeXs,
    color: colors.textMuted,
  },
  // DS Input is block-shaped (fills its container); the bar needs a compact
  // box, so the constraint lives on this wrapper, not inside the DS.
  num: {
    width: sizes.fieldMinWSm,
  },
});

type DsSize = "default" | "small";

interface VerbContext {
  readonly size: DsSize;
  /** Narrow viewport: drop the width chips, keep W×H. */
  readonly narrow: boolean;
}

/** Copy the item right below itself and select the copy. Media refers to
 *  content-addressed blobs, so a shallow copy of contentProps is a faithful
 *  duplicate; a section block (a nested grid) goes through the deep copy in
 *  sectionDuplicate. */
function duplicateItem(gridId: string, itemId: string): string | null {
  const st = useGridStore.getState();
  const item = st.grids[gridId]?.items[itemId];
  if (!item) return null;
  if (item.contentType === "grid")
    return duplicateSection(gridId, itemId) ?? null;
  if (item.contentType === "inline-grid")
    return duplicateInline(gridId, itemId) ?? null;
  const contentProps: Record<string, unknown> = {
    ...(item.contentProps ?? {}),
  };
  // An input's name is its identity in the form (the section): a copy gets
  // a fresh one, allocated after any earlier copy was added.
  if (item.contentType === "input")
    contentProps["name"] = allocateFieldName(gridId);
  return st.addItem(gridId, {
    position: { ...item.position, y: item.position.y + item.position.h },
    contentType: item.contentType,
    contentProps,
    constraints: { ...item.constraints },
  });
}

function BoundariesToggle({
  gridId,
  size,
}: {
  gridId: string | null;
  size: DsSize;
}): React.ReactElement | null {
  const revealed = useRevealedBoundaries();
  if (!gridId) return null;
  const on = revealed === gridId;
  return (
    <Toggle
      size={size}
      pressed={on}
      onPressedChange={(next) =>
        next ? revealBoundaries(gridId) : hideBoundaries()
      }
      aria-label="Show boundaries between blocks"
    >
      Boundaries
    </Toggle>
  );
}

function SizeGroup({
  view,
  ctx,
}: {
  view: WidgetSelection;
  ctx: VerbContext;
}): React.ReactElement | null {
  const metrics = useResizeMetricsApi();
  const columns = useGridStore(
    (st) => st.grids[view.gridId]?.config.columns ?? 12,
  );
  if (view.item.constraints.noResize) return null;
  const position = view.item.position as GridPosition;
  const constraints = view.item.constraints as SizeConstraints;
  const active = activeWidthPreset(position, columns);

  const commit = (next: GridPosition, verb: "preset" | "numeric") => {
    useGridStore.getState().resizeItem(view.gridId, view.itemId, next);
    // Which path got used is the signal that says whether the handle
    // affordance is still wrong however big the target is.
    metrics.reportResizeVerb(verb);
  };
  const commitSize =
    (patch: (n: number) => Partial<GridPosition>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const n = Number(e.target.value);
      if (!Number.isFinite(n)) return;
      commit(
        applyExplicitSize(position, patch(n), columns, constraints),
        "numeric",
      );
    };

  return (
    <html.div style={s.group} aria-label="Size" role="group">
      {!ctx.narrow && (
        <ToggleGroup
          multiple={false}
          size={ctx.size}
          value={active ? [active] : []}
          onValueChange={(next) => {
            const preset = next[0] as WidthPreset | undefined;
            // Single-select groups report [] when the pressed chip is clicked
            // again; "un-choosing" a width has no meaning, so ignore it.
            if (!preset) return;
            commit(
              applyWidthPreset(position, preset, columns, constraints),
              "preset",
            );
          }}
        >
          {WIDTH_PRESETS.map((preset) => (
            <Toggle
              key={preset.id}
              value={preset.id}
              aria-label={`Width ${preset.label}`}
            >
              {preset.label}
            </Toggle>
          ))}
        </ToggleGroup>
      )}
      <html.label style={s.field}>
        <html.span style={s.fieldLabel}>W</html.span>
        <html.div style={s.num}>
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            max={columns}
            value={String(position.w)}
            aria-label="Width in columns"
            onChange={commitSize((w) => ({ w }))}
          />
        </html.div>
      </html.label>
      <html.label style={s.field}>
        <html.span style={s.fieldLabel}>H</html.span>
        <html.div style={s.num}>
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            value={String(position.h)}
            aria-label="Height in rows"
            onChange={commitSize((h) => ({ h }))}
          />
        </html.div>
      </html.label>
    </html.div>
  );
}

/**
 * A button's own width in its cell: Hug (its label's width) or Fill (the
 * cell's). Next to the cell's size because that is where a person resizing
 * the cell looks; the label keeps its size either way, and a taller cell only
 * adds space around the button (its vertical placement is in its settings).
 */
function ButtonWidthGroup({
  view,
  size,
}: {
  view: WidgetSelection;
  size: VerbContext["size"];
}): React.ReactElement {
  const width = (view.item.contentProps as { width?: unknown } | undefined)
    ?.width;
  return (
    <html.div style={s.group} role="group" aria-label="Button width">
      <html.span style={s.fieldLabel}>Button</html.span>
      <ToggleGroup
        multiple={false}
        size={size}
        value={[width === "fill" ? "fill" : "hug"]}
        onValueChange={(next) => {
          // Pressing the active option empties a single group: keep it.
          const v = next[0];
          if (v !== "hug" && v !== "fill") return;
          flushPendingEdits();
          patchContentProps(view.gridId, view.itemId, { width: v });
        }}
      >
        <Toggle value="hug" aria-label="Hug its label">
          Hug
        </Toggle>
        <Toggle value="fill" aria-label="Fill the cell's width">
          Fill
        </Toggle>
      </ToggleGroup>
    </html.div>
  );
}

function WidgetVerbs({
  view,
  ctx,
}: {
  view: WidgetSelection;
  ctx: VerbContext;
}): React.ReactElement {
  const editLabel = editVerbFor(
    view.contentType,
    view.item.contentProps as Record<string, unknown> | undefined,
  );
  const locked = !!(view.item.constraints as { noDelete?: boolean }).noDelete;
  const hasSettings = registeredWidgetSettings(view.contentType) !== undefined;
  const hasQuickEdit =
    registeredWidgetQuickEdit(view.contentType) !== undefined;
  const kindName = widgetKindName(
    view.contentType,
    view.item.contentProps as Record<string, unknown> | undefined,
  );
  return (
    <>
      {editLabel && (
        <Button
          size={ctx.size}
          variant="primary"
          onClick={() => requestEdit(view.itemId)}
        >
          {editLabel}
        </Button>
      )}
      {hasQuickEdit && (
        <Button
          size={ctx.size}
          variant={editLabel ? "secondary" : "primary"}
          onClick={() => openQuickEdit(view.gridId, view.itemId)}
        >
          Quick edit
        </Button>
      )}
      {hasSettings && (
        <Button
          size={ctx.size}
          variant={editLabel || hasQuickEdit ? "secondary" : "primary"}
          onClick={() => openSettings(view.gridId, view.itemId)}
        >
          All settings
        </Button>
      )}
      {!locked && (
        <Button
          size={ctx.size}
          variant="secondary"
          onClick={() => deleteSelection(view)}
          aria-label={`Delete ${kindName.toLowerCase()}`}
        >
          Delete
        </Button>
      )}
      <Button
        size={ctx.size}
        variant="secondary"
        onClick={() => {
          const id = duplicateItem(view.gridId, view.itemId);
          if (id) useGridStore.getState().setSelection(view.gridId, [id]);
        }}
      >
        Duplicate
      </Button>
      <SizeGroup view={view} ctx={ctx} />
      {view.contentType === "button" && (
        <ButtonWidthGroup view={view} size={ctx.size} />
      )}
      <html.div style={s.group}>
        <BoundariesToggle
          gridId={view.parent ? view.gridId : null}
          size={ctx.size}
        />
        {view.parent && (
          <Button
            size={ctx.size}
            variant="secondary"
            onClick={() => selectParent(view)}
            aria-label={`Select the ${containerName(view.parent).toLowerCase()} around this block`}
          >
            {containerName(view.parent)}
          </Button>
        )}
      </html.div>
    </>
  );
}

const GAP_STEP = 4;

function GapField({
  axis,
  label,
  value,
  onChange,
  ctx,
}: {
  axis: "x" | "y";
  label: string;
  value: number;
  onChange: (n: number) => void;
  ctx: VerbContext;
}): React.ReactElement {
  const noun =
    axis === "x"
      ? "column gap"
      : label === "Section spacing"
        ? "section spacing"
        : "row gap";
  return (
    <html.label style={s.field}>
      <html.span style={s.fieldLabel}>{label}</html.span>
      <Button
        size={ctx.size}
        variant="secondary"
        aria-label={`Decrease ${noun}`}
        disabled={value <= 0}
        onClick={() => onChange(Math.max(0, value - GAP_STEP))}
      >
        −
      </Button>
      <html.div style={s.num}>
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          value={String(value)}
          aria-label={`${noun[0]!.toUpperCase()}${noun.slice(1)} in pixels`}
          onChange={(e) => {
            const n = Number(e.target.value);
            if (Number.isFinite(n)) onChange(n);
          }}
        />
      </html.div>
      <Button
        size={ctx.size}
        variant="secondary"
        aria-label={`Increase ${noun}`}
        onClick={() => onChange(value + GAP_STEP)}
      >
        +
      </Button>
    </html.label>
  );
}

function SectionVerbs({
  view,
  ctx,
}: {
  view: SectionSelection;
  ctx: VerbContext;
}): React.ReactElement {
  const nested = view.nestedGridId;
  const config = useGridStore((st) =>
    nested ? (st.grids[nested]?.config ?? null) : null,
  );
  const pageItems = useGridStore((st) => st.grids[view.gridId]?.items);
  const items = pageItems ?? {};
  // The engine clamps (column gap against container width, row gap against
  // row height), so the field is driven from the committed value — typing an
  // out-of-range number visibly snaps back rather than silently disagreeing
  // with the canvas.
  const gap = config ? gapOf(config) : null;
  const setGap = (axis: "x" | "y") => (n: number) => {
    if (!nested) return;
    useGridStore.getState().setGridGap(nested, { [axis]: n });
  };
  const move = (direction: "up" | "down") => {
    const moves = swapSection(items, view.itemId, direction);
    if (!moves) return;
    const st = useGridStore.getState();
    for (const m of moves) {
      const it = st.grids[view.gridId]?.items[m.id];
      if (it)
        st.updateItem(view.gridId, m.id, {
          position: { ...it.position, y: m.y },
        });
    }
    st.reflow(view.gridId);
  };

  return (
    <>
      {gap && (
        <html.div style={s.group} role="group" aria-label="Gap">
          <GapField
            axis="x"
            label="Gap ↔"
            value={gap.x}
            onChange={setGap("x")}
            ctx={ctx}
          />
          <GapField
            axis="y"
            label="↕"
            value={gap.y}
            onChange={setGap("y")}
            ctx={ctx}
          />
        </html.div>
      )}
      <html.div style={s.group}>
        <BoundariesToggle gridId={nested} size={ctx.size} />
        <Button
          size={ctx.size}
          variant="secondary"
          aria-label="Move section up"
          disabled={!canMoveSection(items, view.itemId, "up")}
          onClick={() => move("up")}
        >
          ↑ Up
        </Button>
        <Button
          size={ctx.size}
          variant="secondary"
          aria-label="Move section down"
          disabled={!canMoveSection(items, view.itemId, "down")}
          onClick={() => move("down")}
        >
          ↓ Down
        </Button>
        <Button
          size={ctx.size}
          variant="secondary"
          aria-label={`Duplicate ${view.label} section`}
          onClick={() => {
            const id = duplicateItem(view.gridId, view.itemId);
            if (id) useGridStore.getState().setSelection(view.gridId, [id]);
          }}
        >
          Duplicate
        </Button>
        <Button
          size={ctx.size}
          variant="secondary"
          aria-label={`Delete ${view.label} section`}
          onClick={() => deleteSelection(view)}
        >
          Delete
        </Button>
      </html.div>
    </>
  );
}

/** Inline gap choices: the Duro spacing scale (sm/md/lg), in the px the
 *  nested grid's `gapX` is stored in. */
const INLINE_GAPS = [
  { id: "sm", label: "S", name: "small", px: 8 },
  { id: "md", label: "M", name: "medium", px: 16 },
  { id: "lg", label: "L", name: "large", px: 24 },
] as const;

/** The container a block sits in, as its select-parent button names it:
 *  an Inline's blocks select their Inline, a section's blocks the section
 *  — the way out of a full Inline, which leaves no empty row to press. */
function containerName(parent: {
  readonly gridId: string;
  readonly itemId: string;
}): "Inline" | "Section" {
  const it = useGridStore.getState().grids[parent.gridId]?.items[parent.itemId];
  return it?.contentType === "inline-grid" ? "Inline" : "Section";
}

const INLINE_ALIGNS = [
  { id: "start", glyph: "⇤", name: "Align start" },
  { id: "center", glyph: "↔", name: "Align center" },
  { id: "end", glyph: "⇥", name: "Align end" },
] as const;

function InlineVerbs({
  view,
  ctx,
}: {
  view: InlineSelection;
  ctx: VerbContext;
}): React.ReactElement {
  const nested = view.nestedGridId;
  const config = useGridStore((st) =>
    nested ? (st.grids[nested]?.config ?? null) : null,
  );
  const gapX = config ? gapOf(config).x : null;
  const activeGap = INLINE_GAPS.find((g) => g.px === gapX);
  const setJustify = (justify: InlineJustify) => {
    flushPendingEdits();
    patchContentProps(view.gridId, view.itemId, { justify });
    if (nested) alignInlineGrid(nested);
  };
  const setGap = (px: number) => {
    if (nested) useGridStore.getState().setGridGap(nested, { x: px });
  };
  return (
    <>
      <html.div style={s.group} role="group" aria-label="Alignment">
        <ToggleGroup
          multiple={false}
          size={ctx.size}
          value={[view.justify]}
          onValueChange={(next) => {
            // Pressing the active chip empties a single group: keep it.
            const v = next[0] as InlineJustify | undefined;
            if (v) setJustify(v);
          }}
        >
          {INLINE_ALIGNS.map((a) => (
            <Toggle key={a.id} value={a.id} aria-label={a.name}>
              {a.glyph}
            </Toggle>
          ))}
        </ToggleGroup>
      </html.div>
      {gapX !== null && (
        <html.div style={s.group} role="group" aria-label="Gap">
          <ToggleGroup
            multiple={false}
            size={ctx.size}
            value={activeGap ? [activeGap.id] : []}
            onValueChange={(next) => {
              const g = INLINE_GAPS.find((k) => k.id === next[0]);
              if (g) setGap(g.px);
            }}
          >
            {INLINE_GAPS.map((g) => (
              <Toggle key={g.id} value={g.id} aria-label={`Gap ${g.name}`}>
                {g.label}
              </Toggle>
            ))}
          </ToggleGroup>
        </html.div>
      )}
      <Button
        size={ctx.size}
        variant="secondary"
        onClick={() => openSettings(view.gridId, view.itemId)}
      >
        All settings
      </Button>
      <Button
        size={ctx.size}
        variant="secondary"
        aria-label="Duplicate inline"
        onClick={() => {
          const id = duplicateInline(view.gridId, view.itemId);
          if (id) useGridStore.getState().setSelection(view.gridId, [id]);
        }}
      >
        Duplicate
      </Button>
      <Button
        size={ctx.size}
        variant="secondary"
        aria-label="Delete inline"
        onClick={() => deleteSelection(view)}
      >
        Delete
      </Button>
    </>
  );
}

function MultiVerbs({
  view,
  ctx,
}: {
  view: MultiSelection;
  ctx: VerbContext;
}): React.ReactElement {
  return (
    <>
      <Button
        size={ctx.size}
        variant="secondary"
        onClick={() => deleteSelection(view)}
        aria-label="Delete selected blocks"
      >
        Delete
      </Button>
      <Button
        size={ctx.size}
        variant="secondary"
        onClick={() => {
          const ids = view.itemIds
            .map((id) => duplicateItem(view.gridId, id))
            .filter((id): id is string => id != null);
          if (ids.length)
            useGridStore.getState().setSelection(view.gridId, ids);
        }}
      >
        Duplicate
      </Button>
      {view.parent && (
        <Button
          size={ctx.size}
          variant="secondary"
          onClick={() => selectParent(view)}
          aria-label={`Select the ${containerName(view.parent).toLowerCase()} around these blocks`}
        >
          {containerName(view.parent)}
        </Button>
      )}
    </>
  );
}

function PageVerbs({
  page,
  ctx,
}: {
  page: SelectedPage;
  ctx: VerbContext;
}): React.ReactElement | null {
  const config = useGridStore((st) => st.grids[page.pageId]?.config ?? null);
  if (!config) return null;
  const gap = gapOf(config);
  return (
    <html.div style={s.group} role="group" aria-label="Section spacing">
      <GapField
        axis="y"
        label="Section spacing"
        value={gap.y}
        onChange={(n) =>
          useGridStore.getState().setGridGap(page.pageId, { y: n })
        }
        ctx={ctx}
      />
    </html.div>
  );
}

function labelFor(view: SelectionView): string {
  switch (view.kind) {
    case "widget":
      return widgetKindName(
        view.contentType,
        view.item.contentProps as Record<string, unknown> | undefined,
      );
    case "section":
      return `${view.label} section`;
    case "inline":
      return "Inline";
    case "multi":
      return `${view.itemIds.length} blocks`;
    default:
      return "";
  }
}

/** Publish the bar's rendered height for the pills stacked above it. The DS
 *  owns the toolbar element; we measure it from a child ref. */
function useBarMeasure(
  visible: boolean,
): React.RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!visible) {
      publishBarHeight(0);
      return;
    }
    const bar = ref.current?.closest<HTMLElement>('[role="toolbar"]');
    if (!bar || typeof ResizeObserver === "undefined") return;
    const measure = () => publishBarHeight(bar.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    return () => {
      ro.disconnect();
      publishBarHeight(0);
    };
  }, [visible]);
  return ref;
}

export function SelectionActionBar(): React.ReactElement | null {
  const view = useSelectionView();
  const page = useSelectedPage();
  const coarse = usePointerCoarse();
  const narrow = useIsMobile();
  // The docked Inspector's width; on a phone it is a bottom drawer, so none.
  const docked = useDockedInset();
  const dockedInset = narrow ? 0 : docked;
  useSelectionKeys(view);
  // The grid selection wins; the page state fills in only when nothing
  // inside the page is selected.
  const pageOnly = view.kind === "none" && page != null;
  const ref = useBarMeasure(view.kind !== "none" || pageOnly);

  // ActionBar hides itself at count 0, but returning null also spares the
  // hook-free subtree entirely.
  if (view.kind === "none" && !pageOnly) return null;

  const ctx: VerbContext = { size: coarse ? "default" : "small", narrow };
  const count = view.kind === "multi" ? view.itemIds.length : 1;
  const label = pageOnly ? `${page.label} page` : labelFor(view);

  return (
    <ActionBar
      selectedItemCount={count}
      selectedLabel={() => label}
      bottomOffset={BAR_BOTTOM}
      // Beside the docked Inspector, not under it.
      insetInlineEnd={dockedInset}
      // A page node is deselected by the canvas (click the empty canvas);
      // the ✕ only clears what the bar itself owns.
      dismissible={!pageOnly}
      onClearSelection={() => useGridStore.getState().clearSelection()}
    >
      <html.div
        ref={ref}
        style={s.verbs}
        role="group"
        // selectionBar.css keys its z-index + coarse-pointer rules on this.
        aria-label="Selection verbs"
      >
        {pageOnly && <PageVerbs page={page} ctx={ctx} />}
        {view.kind === "widget" && <WidgetVerbs view={view} ctx={ctx} />}
        {view.kind === "section" && <SectionVerbs view={view} ctx={ctx} />}
        {view.kind === "inline" && <InlineVerbs view={view} ctx={ctx} />}
        {view.kind === "multi" && <MultiVerbs view={view} ctx={ctx} />}
      </html.div>
    </ActionBar>
  );
}
